import React, { useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { X, Minus, Plus, Trash2, ShoppingBag } from "lucide-react";
import { inr } from "@/lib/api";
import { useCart } from "@/context/CartContext";

export default function CartDrawer() {
  const { items, subtotal, updateQty, removeItem, lineKey, drawerOpen, setDrawerOpen, count } = useCart();
  const navigate = useNavigate();
  const go = (path) => { setDrawerOpen(false); navigate(path); };
  const metaFor = useMemo(
    () => Object.fromEntries(items.map((it) => [lineKey(it), [it.color, it.size].filter(Boolean).join(" · ")])),
    [items, lineKey],
  );

  if (!drawerOpen) return null;

  return (
    <div className="fixed inset-0 z-[65]" data-testid="cart-drawer">
      <div className="absolute inset-0 bg-espresso/40" onClick={() => setDrawerOpen(false)} />
      <div className="absolute right-0 top-0 h-full w-full max-w-md bg-ivory shadow-2xl flex flex-col fade-in-up">
        <div className="flex items-center justify-between px-6 py-5 border-b border-champagne/20">
          <h3 className="font-serif text-xl text-espresso">Shopping Bag ({count})</h3>
          <button onClick={() => setDrawerOpen(false)} data-testid="cart-drawer-close"><X className="w-6 h-6" /></button>
        </div>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center px-6">
            <ShoppingBag className="w-12 h-12 text-champagne/50 mb-4" />
            <p className="font-serif text-2xl text-espresso mb-2">Your bag is empty</p>
            <p className="text-sm text-espresso/50 mb-6">Discover timeless modest fashion.</p>
            <button onClick={() => go("/shop")} className="bg-espresso text-ivory px-8 py-3 rounded-full text-sm">Start Shopping</button>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
              {items.map((it) => {
                const k = lineKey(it);
                return (
                  <div key={k} className="flex gap-4" data-testid={`cart-item-${it.slug}`}>
                    <img src={it.image} alt={it.name} className="w-20 h-24 object-cover rounded-xl" />
                    <div className="flex-1">
                      <div className="flex justify-between">
                        <Link to={`/product/${it.slug}`} onClick={() => setDrawerOpen(false)} className="font-serif text-base text-espresso leading-tight pr-2">{it.name}</Link>
                        <button onClick={() => removeItem(k)} data-testid={`cart-remove-${it.slug}`}><Trash2 className="w-4 h-4 text-espresso/40 hover:text-[#a13b2f]" /></button>
                      </div>
                      <p className="text-xs text-espresso/50 mt-1">{metaFor[k]}</p>
                      <div className="flex items-center justify-between mt-2">
                        <div className="flex items-center border border-espresso/15 rounded-full">
                          <button onClick={() => updateQty(k, it.quantity - 1)} className="w-7 h-7 flex items-center justify-center"><Minus className="w-3 h-3" /></button>
                          <span className="w-6 text-center text-sm">{it.quantity}</span>
                          <button onClick={() => updateQty(k, it.quantity + 1)} className="w-7 h-7 flex items-center justify-center"><Plus className="w-3 h-3" /></button>
                        </div>
                        <span className="font-semibold text-sm text-espresso">{inr(it.price * it.quantity)}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="border-t border-champagne/20 px-6 py-5 space-y-4">
              <div className="flex justify-between text-sm">
                <span className="text-espresso/60">Subtotal</span>
                <span className="font-semibold text-espresso" data-testid="cart-drawer-subtotal">{inr(subtotal)}</span>
              </div>
              <button onClick={() => go("/checkout")} className="w-full bg-espresso text-ivory py-3.5 rounded-full text-sm font-medium hover:bg-espresso/90" data-testid="cart-drawer-checkout">Checkout</button>
              <button onClick={() => go("/cart")} className="w-full border border-espresso/20 py-3 rounded-full text-sm">View Bag</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
